import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { ReactSVG } from "react-svg";
import { observer } from "mobx-react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useResponsive } from "@/hooks/use-responsive";
import { useProductFeatures } from "@/hooks/useProductFeatures";
import { usePlanAvailability } from "@/hooks/usePlanAvailability";
import {
  useGetEducatorDownloads,
  useAwards,
  useCoursesContent,
} from "@/hooks/useCMSData";
import { usePathname } from "@/routes/hooks";
import { useLocales } from "@/locales";

import Logo from "@/components/logo";
import Scrollbar from "@/components/scrollbar";
import Image from "@/components/image";
import NavigationIdentifier from "@/components/navigation-identifier";

import { useGetDistrictById } from "../../hooks/useGetDistrictById";
import { useGetDistrictLogo } from "../../hooks/useGetDistrictLogo";
import { useGetMyAwards } from "../../hooks/useAwards";
import { NAV } from "../config-layout";

import { Icon, NavItem, NavSubItem, NavSection } from "./nav-components";

// Icons
import DashboardIcon from "@/assets/icons/navbar/ic_dashboard.svg";
import HatIcon from "@/assets/icons/navbar/hat.svg";
import LadderIcon from "@/assets/icons/navbar/ladder.svg";
import StarIcon from "@/assets/icons/navbar/star.svg";
import MegaphoneIcon from "@/assets/icons/navbar/megaphone.svg";
import GroupIcon from "@/assets/icons/navbar/ic-solar_group.svg";
import CourseIcon from "@/assets/icons/navbar/ic-course.svg";
import GroupsIcon from "@/assets/icons/navbar/groups.svg";
import UserIcon from "@/assets/icons/navbar/ic-user-group.svg";
import SiteIcon from "@/assets/icons/navbar/ic_banking.svg";
import CareerPlanFlagIcon from "@/assets/icons/navbar/career-plan-flag.svg";
import ReportsIcon from "@/assets/icons/navbar/ic_analytics.svg";
import WorkSitesIcon from "@/assets/icons/navbar/ic-solar_calendar-mark.svg";
import GetStartedIcon from "@/assets/icons/navbar/get-started.svg";
import FileIcon from "@/assets/icons/navbar/ic_file.svg";
import ClimbersIcon from "@/assets/icons/navbar/ic-solar_climber.svg";
import ExternalIcon from "@/assets/icons/navbar/ic_external.svg";
import GearIcon from "@/assets/icons/navbar/ic_gear.svg";
import SidebarDuotoneIcon from "@/assets/icons/navbar/ic-sidebar-duotone.svg";

// ─────────────────────────────────────────────────────────────────────────────
// CLIMBER NAVIGATION
// ─────────────────────────────────────────────────────────────────────────────

function ClimberNavigation() {
  const { matchesProductPlans, msPlans, proskillPlans, wblPlans } =
    useProductFeatures();
  const { awards } = useAwards();
  const { data: myEarnedAwards } = useGetMyAwards();
  const { courses } = useCoursesContent();

  const pathPrefix = msPlans.length > 0 ? "/ms" : "";
  const isWblClimber = matchesProductPlans([
    "wbl-staff",
    "wbl-ux",
    "wbl-practicum",
    "wbl-practicum-staff",
  ]);
  const hasCourses = courses.length > 0;
  const hasAwards = awards?.length > 0 || myEarnedAwards?.length > 0;

  return (
    <NavSection>
      <NavItem
        title="Dashboard"
        icon={<Icon src={DashboardIcon} />}
        path={`${pathPrefix}/climber/dashboard`}
      />

      {!isWblClimber && (
        <NavItem
          title="Hats"
          icon={<Icon src={HatIcon} />}
          path={`${pathPrefix}/hats`}
        />
      )}
      <NavItem
        title="Career Climbs"
        icon={<Icon src={CourseIcon} />}
        path={`${pathPrefix}/career-climbs`}
      />
      <NavItem
        title="Ladders"
        icon={<Icon src={LadderIcon} />}
        path={`${pathPrefix}/ladders`}
      />

      {hasCourses && (
        <NavItem
          title="Courses"
          icon={<Icon src={GroupIcon} />}
          path={`${pathPrefix}/courses`}
        />
      )}
      {hasAwards && (
        <NavItem
          title="Achievements"
          icon={<Icon src={StarIcon} />}
          path={`${pathPrefix}/achievements`}
        />
      )}
    </NavSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EDUCATOR NAVIGATION
// ─────────────────────────────────────────────────────────────────────────────

function EducatorNavigation({ currentUser, resourceHubUrl }) {
  const { t } = useLocales();
  const pathname = usePathname();
  const { matchesProductPlans } = useProductFeatures();
  const { educatorDownloads } = useGetEducatorDownloads();
  const { courses } = useCoursesContent();
  // The district's plan availability windows drive the Climber Plans nav item,
  // replacing the hardcoded "Available January" copy.
  const {
    isAvailable: planningOpen,
    isConfigured: planningConfigured,
    opensOn: planningOpensOn,
  } = usePlanAvailability();

  // ─── WHO IS THIS USER? ───
  const role = currentUser?.effectiveRole;
  const isTeacher = role === "teacher";
  const isSchoolStaff = role === "schoolstaff";
  const isDistrictAdmin =
    currentUser?.isDistrictAdmin || role === "districtadmin";
  const isSuperAdmin = currentUser?.isSuperadminWithActiveDistrict;
  // The ACTUAL Cognito role, not the effective one. `role` above is
  // effectiveRole, which becomes "districtadmin" as soon as a Hats staff
  // member opens a district — so anything gated on it disappears exactly when
  // they are working, including the nav that gets them back out.
  const isHatsStaff = currentUser?.role === "hatsstaff";

  // ─── WHAT PRODUCTS DO THEY HAVE? ───
  const isMsStaff = matchesProductPlans(["ms-staff"]);
  const isWblStaff = matchesProductPlans([
    "wbl-staff",
    "wbl-ux",
    "wbl-practicum",
    "wbl-practicum-staff",
  ]);
  const isWfStaff = matchesProductPlans(["wf-staff", "launch-content"]);

  // ─── WHAT CONTENT IS AVAILABLE? ───
  const hasImplementationMaterials = educatorDownloads.some(
    (d) => d.resourceType?.name === "Implementation Materials",
  );
  const hasLessonsContent = educatorDownloads.some(
    (d) => d.resourceType?.name === "Lessons",
  );
  const hasCourses = courses.length > 0;

  // ─── WHAT CAN THEY SEE? ───
  const isStaff = !isTeacher;
  const canViewClimberPlans = isMsStaff;
  const canViewShoutOuts = isMsStaff;
  const canViewSiteVisits = isTeacher && isWblStaff;

  // Users submenu visibility
  const canViewCoachesSubmenu =
    isSchoolStaff || isDistrictAdmin || isSuperAdmin;
  // Staff are the district's own business. School staff used to reach this
  // when they carried canAddPeers; that flag is retired.
  const canViewStaffSubmenu = isDistrictAdmin || isSuperAdmin;
  const isEducator = role !== "climber";
  // Coach Toolkit visibility
  const canViewClimberMode = isMsStaff && isEducator;
  const canViewWblWfContent = isWblStaff || isWfStaff;
  const canViewCourses = canViewWblWfContent && hasCourses;
  const canViewHelpCenter = isWfStaff;
  const showCoachToolkit =
    !!resourceHubUrl ||
    hasImplementationMaterials ||
    hasLessonsContent ||
    canViewClimberMode ||
    canViewWblWfContent;

  // Every item in the sections below is district-scoped, and Hats staff working
  // in their own area have no district for them to act on — they lead to empty
  // pages or a 404. So inside /hatsstaff the Hats Staff section is the whole
  // navigation; step into a district and these come back, behaving exactly as
  // they do for that district's own admin.
  //
  // Keyed on the route rather than on activeDistrictId because the district
  // outlives the visit: it stays set on the record after you leave, so the flag
  // would say "in a district" while you are back at the browser choosing one.
  // Where you are is the honest signal; what you last opened is not.
  const inHatsStaffArea = pathname.startsWith("/hatsstaff");
  const showDistrictScopedNav = !isHatsStaff || !inHatsStaffArea;

  return (
    <>
      {/* DASHBOARD */}
      {showDistrictScopedNav && (
        <NavSection>
          <NavItem
            title="Dashboard"
            icon={<Icon src={DashboardIcon} />}
            path={`/${role}/dashboard`}
          />
        </NavSection>
      )}

      {/* CLASS / GROUP MANAGEMENT */}
      {showDistrictScopedNav && (
        <NavSection
          subheader={
            isDistrictAdmin
              ? "Group Management"
              : t("class_singular") + " Management"
          }
        >
          {isStaff && (
            <NavItem
              title={t("school_plural")}
              icon={<Icon src={SiteIcon} />}
              path="/sites"
            />
          )}

          {isStaff && (
            <NavItem title="Users" icon={<Icon src={UserIcon} />} path="/users">
              {canViewCoachesSubmenu && (
                <NavSubItem title="Coaches" path="users/coaches" />
              )}
              {canViewStaffSubmenu && (
                <NavSubItem title="Staff" path="users/staff" />
              )}
              {isDistrictAdmin && (
                <NavSubItem title="Administrators" path="users/admins" />
              )}
              <NavSubItem title="Climbers" path="users/climbers" />
            </NavItem>
          )}

          {isTeacher && (
            <NavItem
              title={t("class_plural")}
              icon={<Icon src={GroupsIcon} />}
              path="/groups"
            />
          )}
          {isTeacher && (
            <NavItem
              title="Climbers"
              icon={<Icon src={UserIcon} />}
              path="/users/climbers"
            />
          )}

          {canViewClimberPlans && planningConfigured && (
            <NavItem
              title="Climber Plans"
              subtitle={
                planningOpen ? undefined : `Available ${planningOpensOn}`
              }
              icon={<Icon src={CareerPlanFlagIcon} />}
              path={
                isTeacher
                  ? "/staff/career-course-planner"
                  : "/staff/school-plans"
              }
              disabled={!planningOpen}
            />
          )}

          {canViewShoutOuts && (
            <NavItem
              title="Shout Outs"
              icon={<Icon src={MegaphoneIcon} />}
              path="/staff/shout-outs"
            />
          )}

          <NavItem
            title="Reports"
            icon={<Icon src={ReportsIcon} />}
            path="/reports/dashboard"
            activePath="/reports"
          />

          {canViewSiteVisits && (
            <NavItem
              title="Site Visits"
              icon={<Icon src={WorkSitesIcon} />}
              path="/staff/worksites"
            />
          )}
        </NavSection>
      )}

      {/* COACH TOOLKIT */}
      {showDistrictScopedNav && showCoachToolkit && (
        <NavSection subheader="Coach Toolkit">
          {resourceHubUrl && (
            <NavItem
              title="Resource Hub"
              icon={<Icon src={SidebarDuotoneIcon} />}
              path={resourceHubUrl}
              externalLink
            />
          )}

          {hasImplementationMaterials && (
            <NavItem
              title="Get Started"
              icon={<Icon src={GetStartedIcon} />}
              path="/educator-downloads/implementation-materials"
            />
          )}

          {hasLessonsContent && (
            <NavItem
              title="Lessons & Activities"
              icon={<Icon src={FileIcon} />}
              path="/educator-downloads/lessons"
            />
          )}

          {canViewClimberMode && (
            <NavItem
              title="Climber Mode"
              icon={<Icon src={ClimbersIcon} />}
              path="/teacher/ms-climber-experience"
            />
          )}

          {isWfStaff && (
            <NavItem
              title="Hats"
              icon={<Icon src={HatIcon} />}
              path="/climber-experience/hats"
            />
          )}
          {canViewWblWfContent && (
            <NavItem
              title="Career Climbs"
              icon={<Icon src={CourseIcon} />}
              path="/climber-experience/career-climbs"
            />
          )}

          {canViewCourses && (
            <NavItem
              title="Courses"
              icon={<Icon src={GroupIcon} />}
              path="/climber-experience/courses"
            />
          )}

          {canViewHelpCenter && (
            <NavItem
              title="Help Center"
              icon={<Icon src={ExternalIcon} />}
              path="https://help.hatsandladders.com/"
              externalLink
            />
          )}
        </NavSection>
      )}

      {/* HATS & LADDERS INTERNAL STAFF */}
      {isHatsStaff && (
        <NavSection subheader="Hats Staff">
          <NavItem
            title="Internal Dashboard"
            icon={<Icon src={GearIcon} />}
            path="/hatsstaff/dashboard"
          />
          <NavItem
            title="Districts"
            icon={<Icon src={SiteIcon} />}
            path="/hatsstaff/districts"
          />
          {/* The same people pages a district admin gets, plus District
              Administrators. They are district-scoped pages, so they only have
              anything to show once a district has been opened from the
              Districts browser — the sub-item is the destination, the district
              is the scope. */}
          <NavItem
            title="Users"
            icon={<Icon src={UserIcon} />}
            path="/hatsstaff/users"
          >
            <NavSubItem title="Coaches" path="hatsstaff/users/coaches" />
            <NavSubItem title="Staff" path="hatsstaff/users/staff" />
            <NavSubItem
              title="District Administrators"
              path="hatsstaff/users/admins"
            />
            <NavSubItem title="Climbers" path="hatsstaff/users/climbers" />
          </NavItem>
        </NavSection>
      )}

      {/* CONFIGURATION (District Admin only -- NOT Super Admins!) */}
      {/* District settings are district-scoped like everything above, so they
          follow the same rule: not shown while Hats staff are in their own
          area, present once they are inside a district. */}
      {showDistrictScopedNav && isDistrictAdmin && (
        <NavSection subheader="Configuration">
          <NavItem
            title="Settings"
            icon={<Icon src={GearIcon} />}
            path="/settings"
          />
        </NavSection>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DISTRICT LOGO
// ─────────────────────────────────────────────────────────────────────────────

function DistrictLogo({ logo, districtName }) {
  if (!logo?.logo) return null;

  const isSvg = logo.logo.trim().startsWith("<svg");

  return (
    <Box sx={{ textAlign: "center", mt: 4 }}>
      {isSvg ? (
        <Box sx={{ minHeight: "60px", maxHeight: "60px" }}>
          <ReactSVG
            src={`data:image/svg+xml;utf8,${encodeURIComponent(logo.logo)}`}
          />
        </Box>
      ) : (
        <Image
          src={logo.logo}
          alt={`${districtName} logo`}
          sx={{ height: "60px" }}
        />
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const NavVertical = observer(({ openNav, onCloseNav }) => {
  const currentUser = useCurrentUser();
  const pathname = usePathname();
  const lgUp = useResponsive("up", "lg");
  const { onChangeLang } = useLocales();
  const langSetRef = useRef(null);

  const { data: district } = useGetDistrictById(currentUser?.districtId);
  const { data: logo } = useGetDistrictLogo(district?.id, !!district);

  const isClimber = currentUser?.role === "climber";

  useEffect(() => {
    if (openNav) onCloseNav();
  }, [pathname]);

  useEffect(() => {
    if (district?.nomenclature) {
      const lang = `en_${district.nomenclature}`;
      if (langSetRef.current !== lang) {
        langSetRef.current = lang;
        onChangeLang(lang);
      }
    }
  }, [district?.nomenclature]);

  const content = (
    // The nav is a flex column: the identifier block carries `mt: auto`, so it
    // sits at the bottom when there is room to spare and falls back to flowing
    // directly under the last nav item once the menu fills the viewport.
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          // Deliberately the viewport and not `100%`. simplebar-react is used
          // without its stylesheet, so its wrappers collapse to the height of
          // their content and it sets `height: auto` inline on the scrolling
          // one — a percentage here would resolve against an auto-height
          // parent, i.e. to nothing, leaving `mt: auto` below no free space to
          // push into. The nav is always full-height (fixed on desktop, a
          // full-height Drawer on mobile), so the viewport is the honest
          // measure. Min-height, so a long menu still grows and scrolls
          // instead of being clipped.
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Logo sx={{ mt: 3, ml: 4, mb: 1, flexShrink: 0 }} />

      <Stack sx={{ flexShrink: 0 }}>
        {isClimber ? (
          <ClimberNavigation />
        ) : (
          <EducatorNavigation
            currentUser={currentUser}
            resourceHubUrl={district?.resourceHubUrl ?? null}
          />
        )}
      </Stack>

      <Box sx={{ mt: "auto", pt: 2, pb: 6, flexShrink: 0 }}>
        <NavigationIdentifier />
        <DistrictLogo logo={logo} districtName={district?.name} />
      </Box>
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { lg: 0 }, width: { lg: NAV.W_VERTICAL } }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: "fixed",
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {content}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{ sx: { width: NAV.W_VERTICAL } }}
        >
          {content}
        </Drawer>
      )}
    </Box>
  );
});

NavVertical.propTypes = {
  onCloseNav: PropTypes.func,
  openNav: PropTypes.bool,
};

export default NavVertical;
